import React, { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';

import { init } from '@noriginmedia/norigin-spatial-navigation';

import { IS_BROWSER } from './utils/is-browser';
import AuthenticatedApp from './authenticated-app';
import CookieBanner from './components/cookie-banner/cookie-banner';
import { GDPRDialog } from './components/gdpr-dialog/gdpr-dialog';
import { PlayerProvider } from './components/player/_contexts/player-context';
import { StreamPlayerProvider } from './components/player/vod-wrapper/_contexts/stream-player-context';
import { DialogProvider, StreamProvider } from './contexts';
import { ErrorProvider } from './contexts/error-context/error-context';
import useAuthentication from './hooks/use-authentication/use-authentication';
import useCookieConsent from './hooks/use-cookie-consent/use-cookie-consent';
import { initializeGoogleAnalytics } from './utils/google-analytics';
import useGDPR from './hooks/use-gdpr';
import usePerformance from './hooks/usePerformance';
import UnauthenticatedApp from './unauthenticated-app';
import { isExitKey } from '@smart-tv/utils/remoteKeys';
import closeTvApp from '@smart-tv/utils/closeTvApp';
import useEventListener from './hooks/use-event-listener/use-event-listener';
import NetworkLogOverlay from './components/network-log-overlay/network-log-overlay';
import Toast from './components/toast/toast';
import { useNetwork } from './hooks/use-network/use-network';
import ErrorDialog from './components/error-dialog/error-dialog';
import { useError } from './hooks/use-error';
import { setFocus } from '@noriginmedia/norigin-spatial-navigation';

if (process.env.DISABLE_FOCUS_SYSTEM !== 'true') {
  init({
    throttleKeypresses: true,
    throttle: 150
    //debug: true,
    //visualDebug: true
  });
} else {
  document.body.classList.add('focus-disabled');
}

if (IS_BROWSER) {
  document.body.classList.add('browser-mode');
}

interface AppContentProps {
  authenticated: boolean;
  gdprOpen: boolean;
  handleGdprClose: () => void;
}

const AppContent: React.FC<AppContentProps> = ({ authenticated, gdprOpen, handleGdprClose }) => {
  const { isOffline, setIsOffline, checkNetwork } = useNetwork();
  const { refreshAuthState } = useAuthentication();
  const { errorProps } = useError();
  const { cookiesAccepted } = useCookieConsent();

  useEffect(() => {
    if (cookiesAccepted) initializeGoogleAnalytics();
  }, [cookiesAccepted]);

  const handleRetry = React.useCallback(async () => {
    const hasNetwork = await checkNetwork();
    if (!hasNetwork) return;
    refreshAuthState();
    setIsOffline(false);
    setFocus('0-0');
  }, [checkNetwork, refreshAuthState, setIsOffline]);

  const networkErrorProps = {
    title: 'Noget gik galt',
    message: 'Tjek din internetforbindelse og prøv igen.',
    primaryButton: {
      label: 'Prøv igen',
      onClick: handleRetry
    },
    secondaryButton: {
      label: 'Luk appen',
      onClick: () => {
        closeTvApp();
      }
    }
  };
  return (
    <StreamProvider>
      <PlayerProvider>
        <StreamPlayerProvider>
          <DialogProvider>
            <Toast />

            <GDPRDialog open={gdprOpen} onClose={handleGdprClose} />
            {IS_BROWSER && cookiesAccepted === undefined && <CookieBanner />}
            <div style={{ width: '100%', minHeight: '100vh' }}>
              {!isOffline && errorProps != null && <ErrorDialog {...errorProps} />}
              {authenticated ? (
                <AuthenticatedApp />
              ) : isOffline ? (
                <ErrorDialog {...networkErrorProps} />
              ) : (
                <UnauthenticatedApp />
              )}
            </div>
          </DialogProvider>
        </StreamPlayerProvider>
      </PlayerProvider>
    </StreamProvider>
  );
};

const App: React.FC = () => {
  const { authenticated } = useAuthentication();
  const { open: gdprOpen, handleClose: handleGdprClose } = useGDPR();

  usePerformance();

  useEventListener('keydown', (event: KeyboardEvent) => {
    if (isExitKey(event) && !event.defaultPrevented) {
      // Dispatch event to notify stream player to close stream (fire-and-forget)
      window.dispatchEvent(new CustomEvent('app-exiting-cleanup'));
      closeTvApp();
    }
  });
  return (
    <BrowserRouter>
      <ErrorProvider>
        <AppContent
          authenticated={authenticated}
          gdprOpen={gdprOpen}
          handleGdprClose={handleGdprClose}
        />
        <NetworkLogOverlay />
      </ErrorProvider>
    </BrowserRouter>
  );
};

export default App;
